import { Entity } from '../entity.js';
import { EFFECT_TYPES } from '../combat/statusEffects.js';
import {
  abilityWorldScale,
  blinkToward,
  isInAbilityCircle,
  isInAbilityLine,
  isWithinAbilityRange
} from '../combat/abilityArea.js';

export const IRELIA_E_BLADE_RADIUS = 90;
export const IRELIA_E_STUN_SEC = 0.75;
export const IRELIA_E_RANGE = 775;

function pushSkillshot(bucket, caster, tx, ty, gameTime, opts) {
  const angle = Math.atan2(ty - caster.y, tx - caster.x);
  bucket.push({
    id: `${opts.idPrefix}-${caster.id}-${gameTime}`,
    casterId: caster.id,
    team: caster.team,
    x: caster.x,
    y: caster.y,
    vx: Math.cos(angle) * opts.speed,
    vy: Math.sin(angle) * opts.speed,
    range: opts.range,
    traveled: 0,
    damage: opts.damage,
    damageType: opts.damageType || 'physical',
    radius: opts.radius || 16
  });
}

export function createIrelia(id = 'irelia-1', team = 'blue', x = 300, y = 1300) {
  return new Entity({
    id,
    type: 'champion',
    team,
    x,
    y,
    maxHp: 580,
    maxMp: 350,
    ad: 63,
    ap: 0,
    armor: 32,
    mr: 32,
    ms: 335,
    attackRange: 200,
    attackSpeed: 0.656,
    name: 'Irelia',
    radius: 20
  });
}

export function castIreliaQ(caster, tx, ty) {
  if (!caster || caster.isDead || caster.mp < 15) return false;
  caster.mp -= 15;
  blinkToward(caster, tx, ty, 600);
  return true;
}

export function castIreliaW(caster) {
  if (!caster || caster.isDead || caster.mp < 70) return false;
  caster.mp -= 70;
  caster.buffs = caster.buffs || [];
  caster.buffs.push({ id: 'defiant_dance', damageReduction: 0.4, duration: 1.5 });
  return true;
}

/** Apply Flawless Duet hits using blade mark radii + corridor between blades. */
export function applyIreliaBladeMarks(caster, entities = []) {
  const marks = caster?.ireliaBladeMarks;
  if (!marks || marks.length < 2) return 0;
  const [a, b] = marks;
  const angle = Math.atan2(b.y - a.y, b.x - a.x);
  const span = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const scale = abilityWorldScale(caster);
  const lolSpan = span / scale;
  const bladeR = marks[0].radius || IRELIA_E_BLADE_RADIUS;
  const damage = 70 + (caster.ad || 0) * 0.7;
  let hits = 0;
  for (const e of entities || []) {
    if (!e || e.isDead || e.team === caster.team) continue;
    const nearBlade = marks.some((m) => isInAbilityCircle(e, m.x, m.y, bladeR, caster));
    const onLine = isInAbilityLine(e, a.x, a.y, angle, lolSpan, bladeR, caster);
    if (!nearBlade && !onLine) continue;
    e.takeDamage(damage, caster, 'physical');
    e.statusManager?.addEffect?.(EFFECT_TYPES.STUN, IRELIA_E_STUN_SEC, 0, e.tenacity || 0);
    hits += 1;
  }
  return hits;
}

export function castIreliaE(caster, tx, ty, projectiles, gameTime, entities = []) {
  if (!caster || caster.isDead || caster.mp < 50) return false;
  if (!isWithinAbilityRange(caster, { x: tx, y: ty }, IRELIA_E_RANGE, caster)) return false;
  caster.mp -= 50;
  // Flawless Duet: two blade marks (FID-ABL-07) — not a pure skillshot_line.
  const marks = [
    { x: caster.x, y: caster.y },
    { x: tx, y: ty }
  ];
  caster.ireliaBladeMarks = marks.map((m, i) => ({
    id: `irelia-e-blade-${caster.id}-${gameTime}-${i}`,
    x: m.x,
    y: m.y,
    radius: IRELIA_E_BLADE_RADIUS,
    team: caster.team,
    expiresAt: gameTime + 1.5
  }));
  const midX = (caster.x + tx) / 2;
  const midY = (caster.y + ty) / 2;
  pushSkillshot(projectiles, caster, midX, midY, gameTime, {
    idPrefix: 'irelia-e-link',
    speed: 1800,
    range: Math.hypot(tx - caster.x, ty - caster.y) || 1,
    damage: 70 + (caster.ad || 0) * 0.7,
    radius: 24
  });
  applyIreliaBladeMarks(caster, entities);
  return true;
}

export function castIreliaR(caster, tx, ty, projectiles, gameTime) {
  if (!caster || caster.isDead || caster.mp < 100) return false;
  caster.mp -= 100;
  pushSkillshot(projectiles, caster, tx, ty, gameTime, {
    idPrefix: 'irelia-r',
    speed: 2000,
    range: 1000,
    damage: 125 + (caster.ad || 0) * 0.8,
    radius: 160
  });
  return true;
}
